import type { ReactNode } from 'react';

export enum SortingOrder {
    /** Ascending order */
    Asc = 'ascend',
    /** Descending order */
    Desc = 'descend',
}

export type SortOrder = SortingOrder | null;

export interface FilterOption {
    value: string;
    label: ReactNode;
}

export interface Column<Item = any> {
    title?: ReactNode;
    dataIndex: string | string[];
    key: string;
    /** Optional slot identifier to align with parent/child when counts differ */
    slotKey?: string;
    render?: (value: any, record: Item) => ReactNode;
    sorter?: boolean;
    sortOrder?: SortOrder;
    filter?: boolean;
    filterOptions?: FilterOption[];
    filterValue?: string;
    className?: string;
}

export interface ExpandIconProps<Item = any> {
    expanded: boolean;
    onExpand: (record: Item) => void;
    record: Item;
}

export interface ExpandableConfig<Item = any, ChildItem = Item> {
    /** Custom expand icon component */
    expandIcon?: (props: ExpandIconProps<Item>) => ReactNode;
    /** Function to determine if a row is expandable */
    rowExpandable?: (record: Item) => boolean;
    /** Function to render expanded content */
    expandedRowRender?: (record: Item) => ReactNode;
    /** Callback when row is expanded/collapsed */
    onExpand?: (expanded: boolean, record: Item) => void;
    /** Field name in the record that contains child items for automatic expansion */
    childrenField?: string;
    /** Columns to use for rendering child items (defaults to parent columns if not provided) */
    childColumns?: Column<ChildItem>[];
    /** Custom render function for each child item row */
    childRowRender?: (childRecord: ChildItem, parentRecord: Item) => ReactNode;
    /** Whether to show column headers for child rows (defaults to false) */
    showChildHeaders?: boolean;
    /**
     * Canonical slot order used to align parent and child columns when their counts differ.
     * When provided, both parent `columns` and `childColumns` should specify `slotKey` (defaults to `key`).
     */
    slotOrder?: string[];
    /**
     * Explicit mapping from parent slotKey (or key) to an ordered list of child slotKeys (or keys)
     * that should render inside that parent column. If omitted, a best-effort 1:1 mapping by slotKey/key is used.
     */
    childColumnGroups?: Record<string, string[]>;
}

export interface TableProps<Item = any, ChildItem = Item> {
    columns: Column<Item>[];
    subColumns?: SubTableColumn<Item>[];
    expandedGroups?: string[];
    subTitle?: string;
    dataSource?: Item[];
    rowKey?: string | ((item: Item) => string);
    striped?: boolean;
    /** Whether to force equal column widths (default: false for dynamic width) */
    equalWidth?: boolean;
    containerClassName?: string;
    tableClassName?: string;
    onSortChange?: (columnKey?: string, order?: SortOrder) => void;
    onFilterChange?: (columnKey: string, value: string) => void;
    onRowClick?: (record: Item) => void;
    cellClassName?: (colIndex: number, rowIndex: number) => string | undefined;
    rowClassName?: (rowIndex: number, record: Item) => string | undefined;
    headCellClassName?: (colIndex: number) => string | undefined;
    loading?: boolean;
    /** Configuration for expandable rows */
    expandable?: ExpandableConfig<Item, ChildItem>;
}

export interface SorterProps {
    column: Column;
    onSortOrderChange: (key: string, orderValue?: SortOrder) => void;
}

export interface FilterProps {
    column: Column;
    onFilterChange: (key: string, value: string) => void;
}

// Simple SubTable component for rendering subgroups, adjust as needed
export interface SubTableColumn<Item = any> {
    key?: string;
    title: string;
    dataIndex: string;
    className?: string;
    render?: (value: any, record: Item) => ReactNode;
}

export interface SubTableProps<Item = any> {
    subColumns: SubTableColumn<Item>[];
    data: Item[];
}
